import React, { useState } from 'react';
import breakfastBg from '../../../assets/chat/meal/breakfast.png';
import lunchBg from '../../../assets/chat/meal/lunch.png';
import dinnerBg from '../../../assets/chat/meal/dinner.png';
import type { MealContentPayload } from '../../../data-access/api/science/openapi/api';
import { DescriptionBox } from '../DescriptionBox';
import './richContext.scss';

type Props = {
  p: {
    meal_content?: MealContentPayload | null;
    [key: string]: unknown;
  };
  enrichmentPending: boolean;
  onEditMealContent?: () => void;
};

const formatNutrientKey = (key: string): string => {
  const unitSuffixes = ['_mg', '_g', '_ml', '_kcal'];
  for (const suffix of unitSuffixes) {
    if (key.endsWith(suffix)) {
      const unit = suffix.slice(1);
      const name = key.slice(0, -suffix.length).replace(/_/g, ' ');
      return `${name}(${unit})`;
    }
  }
  return key.replace(/_/g, ' ');
};

const mealTypeBgMap = {
  breakfast: breakfastBg,
  lunch: lunchBg,
  dinner: dinnerBg,
};
const inferMealType = (rawTitle?: string): string => {
  const title = typeof rawTitle === 'string' ? rawTitle.toLowerCase() : '';
  if (title.includes('breakfast')) return 'breakfast';
  if (title.includes('dinner')) return 'dinner';
  return 'lunch';
};

const MealSuggestionContent: React.FC<Props> = ({ p, enrichmentPending, onEditMealContent }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [mealInput, setMealInput] = useState('');

  const handleOpenEditor = () => {
    setEditorOpen(true);
    onEditMealContent?.();
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setMealInput('');
  };

  const mealContent =
    p.meal_content && typeof p.meal_content === 'object'
      ? (p.meal_content as MealContentPayload)
      : null;
  const foodItems = Array.isArray(mealContent?.food_items)
    ? (mealContent!.food_items as Array<{ name?: unknown; amount?: unknown }>)
    : [];
  const macroFocus =
    mealContent?.macros && typeof mealContent.macros === 'object'
      ? Object.entries(mealContent.macros as Record<string, unknown>).filter(
          ([, value]) => typeof value === 'number' && value > 0
        )
      : [];
  const microFocus =
    mealContent?.micros && typeof mealContent.micros === 'object'
      ? Object.entries(mealContent.micros as Record<string, unknown>).filter(
          ([, value]) => typeof value === 'number' && value > 0
        )
      : [];
  const title =
    typeof mealContent?.title === 'string' && mealContent.title ? mealContent.title : '';
  const mealType = mealContent?.type ?? inferMealType(title);
  const description =
    typeof mealContent?.description === 'string' && mealContent.description
      ? mealContent.description
      : null;

  return (
    <div className="rc-content-body">
      <div className="rc-meal-suggestion-hero-container">
        <img src={mealTypeBgMap[mealType]} className="rc-meal-suggestion-hero-image" alt="meal" />
      </div>
      <div className="rc-meal-suggestion-text-wrap">
        {!!description && <DescriptionBox description={description} />}
        <div className="rc-pill-row">
          {macroFocus.map(([macro, value]) => (
            <div key={macro} className="rc-tag-pill">
              <span className="rc-tag-pill-text">{`🧬 ${formatNutrientKey(macro)}: ${value}`}</span>
            </div>
          ))}
          {microFocus.map(([micro, value], i) => (
            <div key={`${micro}_${i}`} className="rc-tag-pill">
              <span className="rc-tag-pill-text">{`🌿 ${formatNutrientKey(micro)}${typeof value === 'number' ? `: ${value}` : ''}`}</span>
            </div>
          ))}
        </div>
        <div className="rc-meal-suggestion-bottom">
          {foodItems.slice(0, 4).map((item, i) => (
            <div
              key={`${typeof item?.name === 'string' ? item.name : 'food'}_${i}`}
              className="rc-meal-suggestion-list-row"
            >
              <div className="rc-meal-suggestion-item-text-wrap">
                <span className="rc-meal-suggestion-item-title">
                  {'• '}
                  {typeof item?.name === 'string' ? item.name : ''}
                  {typeof item?.amount === 'number' || typeof item?.amount === 'string'
                    ? ` (${item.amount}${typeof (item as { unit?: unknown }).unit === 'string' ? ` ${(item as { unit?: string }).unit}` : ''})`
                    : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
        {enrichmentPending && (
          <div className="rc-skeleton-group">
            <div className="rc-skeleton-row" />
            <div className="rc-skeleton-row rc-skeleton-row--short" />
          </div>
        )}
      </div>
    </div>
  );
};

export default MealSuggestionContent;
